import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { StandardPinService } from '../../../services/standard-pin.service';
import { StandardPinConfiguration, StandardPinScope, StandardPinEditorScope, StandardPinEditorSnapshot } from '../../../models/standard-pin.model';

const COMPANY = 'company';
const DEPARTMENT_PREFIX = 'department:';
const LOAD_ERROR = 'STANDARD_PINS_LOAD_ERROR';
const SAVE_ERROR = 'STANDARD_PINS_SAVE_ERROR';
const CONFLICT_ERROR = 'STANDARD_PINS_CONFLICT';
const HTTP_CONFLICT = 409;
const EMPTY_TEXT = '';
const NO_ITEMS = 0;

export class StandardAssignmentPin {
  configuration: StandardPinConfiguration;
  loading = false;
  saving = false;
  canAdd = false;
  canRemove = false;
  unavailableReason = EMPTY_TEXT;
  errorKey = EMPTY_TEXT;

  constructor(public scope: StandardPinScope, public standardId: string, public active: boolean) {}

  get checked(): boolean { return this.ids.indexOf(this.standardId) >= NO_ITEMS; }
  get ids(): string[] { return this.configuration ? this.configuration.standards.map(item => item.id) : []; }
  get count(): number { return this.ids.length; }
  get disabled(): boolean {
    return this.loading || this.saving || !!this.errorKey || !this.configuration
      || (this.checked ? !this.canRemove : !this.canAdd || this.count >= this.configuration.limit);
  }
}

@Injectable()
export class StandardAssignmentPins implements OnDestroy {
  rows: { [groupId: string]: StandardAssignmentPin } = {};
  company: StandardAssignmentPin;
  loading = false;
  errorKey = EMPTY_TEXT;
  standardId = EMPTY_TEXT;
  private initialized = false;
  private readonly change$ = new Subject<void>();

  constructor(private pins: StandardPinService) {}

  open(standardId: string, visible: boolean): void {
    if (standardId !== this.standardId) { this.reset(standardId || EMPTY_TEXT); }
    if (visible && this.standardId && !this.initialized) { this.load(); }
  }

  reload(): void {
    if (this.loading || this.saving || !this.standardId) { return; }
    this.reset(this.standardId);
    this.load();
  }

  get saving(): boolean {
    return !!(this.company && this.company.saving) || Object.keys(this.rows).some(id => this.rows[id].saving);
  }

  get departmentRows(): StandardAssignmentPin[] { return Object.keys(this.rows).map(id => this.rows[id]); }

  read(row: StandardAssignmentPin): void {
    if (row.loading || row.saving || row.standardId !== this.standardId) { return; }
    this.reload();
  }

  toggle(row: StandardAssignmentPin): void {
    if (row.disabled || row.standardId !== this.standardId) { return; }
    const ids = row.checked ? row.ids.filter(id => id !== this.standardId) : row.ids.concat(this.standardId);
    row.saving = true;
    this.pins.save(row.scope.scopeId, { version: row.configuration.version, standardIds: ids })
      .takeUntil(this.change$).subscribe(configuration => this.accept(row, configuration),
        error => this.fail(row, error.status === HTTP_CONFLICT ? CONFLICT_ERROR : SAVE_ERROR));
  }

  ngOnDestroy(): void { this.change$.next(); this.change$.complete(); }

  private reset(standardId: string): void {
    this.change$.next();
    this.standardId = standardId;
    this.initialized = false;
    this.rows = {};
    this.company = null;
    this.loading = false;
    this.errorKey = EMPTY_TEXT;
  }

  private load(): void {
    this.initialized = true;
    this.loading = true;
    this.pins.editor(this.standardId).takeUntil(this.change$).subscribe(
      snapshot => this.acceptSnapshot(snapshot), () => this.failLoad()
    );
  }

  private acceptSnapshot(snapshot: StandardPinEditorSnapshot): void {
    if (!snapshot || snapshot.standardId !== this.standardId || !Array.isArray(snapshot.scopes)) {
      this.failLoad();
      return;
    }
    snapshot.scopes.forEach(value => this.acceptScope(value));
    this.loading = false;
  }

  private acceptScope(value: StandardPinEditorScope): void {
    const scope = value.scope;
    const row = new StandardAssignmentPin(scope, this.standardId, value.canAdd || value.checked);
    row.canAdd = value.canAdd;
    row.canRemove = value.canRemove;
    row.unavailableReason = value.unavailableReason || EMPTY_TEXT;
    row.configuration = Object.assign({}, scope, {version: value.version,
      standards: value.standardIds.map(id => ({id: id}))});
    if (scope.scopeId === COMPANY) { this.company = row; return; }
    if (scope.scopeId.indexOf(DEPARTMENT_PREFIX) === NO_ITEMS) {
      this.rows[scope.scopeId.substring(DEPARTMENT_PREFIX.length)] = row;
    }
  }

  private accept(row: StandardAssignmentPin, configuration: StandardPinConfiguration): void {
    if (!configuration || configuration.scopeId !== row.scope.scopeId || !Array.isArray(configuration.standards)) {
      this.fail(row, LOAD_ERROR);
      return;
    }
    row.configuration = configuration;
    row.loading = false;
    row.saving = false;
    row.errorKey = EMPTY_TEXT;
    this.reload();
  }

  private fail(row: StandardAssignmentPin, errorKey: string): void {
    row.loading = false;
    row.saving = false;
    row.errorKey = errorKey;
  }

  private failLoad(): void { this.loading = false; this.errorKey = LOAD_ERROR; }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/standards-management/standard-assignment-pins.ts